/*
** $Id$
**
** Copyright 2018-2019 Port25 Solutions, Inc.
** All rights reserved.
*/


import React, { Component } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import Table from './Table';
import CollapsibleElement from './CollapsibleElement'
import { updateComponent, getFromJson, formatBoolean } from '../util/Utils'
import PropTypes from 'prop-types';
import { LONG_REFRESH_INTERVAL } from '../constants'


function makeTableData(state) {
    return {
        queue: {
            columns: ["Name", "#Rcpt", "#KBytes", "#Conn", "Paused",
                      "Mode", "Backoff Reason", "Next Retry"],
            data: makeQueueDetailData(state),
            title: "Queue Detail"
        },
        errors: {
            columns: ["Time", "Last Error"],
            data: makeErrors(state),
            title: "Last Errors"
        },
        modeEvents: makeModeEvents(state),
        rcptEvents: makeRcptEvents(state),
        vmtaSettings: makeSettings(state, ['data', 'data', 'vmta', 'settings']),
        queueSettings: makeSettings(state, ['data', 'data', 'settings'])
    }
}


function makeQueueDetailData(state) {
    let q = getFromJson(state, ['data', 'data', 'queue'])
    return q ? [[q.name, q.rcp, q.kb, q.conn, q.paused, q.mode,
                 q.backoffReason, q.retryTime]]
             : []
}


function makeErrors(state) {
    let errors = getFromJson(state, ['data', 'data', 'queue', 'errors'], [])
    return errors.map((e) => {
        return [e.time, e.text]
    })
}


function makeRcptEvents(state) {
    let data
    let delivered = 0
    let bounced = 0
    let deferred = 0
    let events = getFromJson(state, ['data', 'data', 'queue', 'rcptEvents'], [])
    data = events.map((e) => {
        // format and counting logic borrowed from how it's done in
        // PowerMTA, @see src/http/QueueDetailServlet.cxx
        let type = formatDsnAction(e.dsnAction)
        if (type === 'delivered') {
            delivered++
        }
        else if (type === 'bounced') {
            bounced++
        }
        else if (type === 'deferred') {
            deferred++
        }
        return [type, e.eventTime, e.dlvSourceIp, e.rcpt, e.jobId,
                e.dsnMta, e.addlInfo]
    })
    return {
        columns: ["Event", "Time", "Source IP", "Address", "Job ID",
                  "Destination MTA", "Additional Information"],
        data: data,
        title: "Deliveries: " + delivered + ", Bounces: " + bounced +
               ", Deferrals: " + deferred
    }
}


function makeModeEvents(state) {
    let data
    let events = getFromJson(state, ['data', 'data', 'queue', 'modeEvents'], [])
    data = events.map((e) => {
        return [e.time, e.text]
    })
    return {
        columns: ["Time", "Event"],
        data: data
    }
}


// exported for testing.
export function formatDsnAction(action) {
    if (action === 'delivered' || action === 'relayed') {
        return 'delivered'
    }
    else if (action === 'failed') {
        return 'bounced'
    }
    else if (action === 'delayed') {
        return 'deferred'
    }
    return action
}


function makeSettings(state, path) {
    let settings = getFromJson(state, path, [])
    let s = Object.keys(settings).map((name) => {
        return [name, settings[name]]
    })
    return {
        columns: ["Name", "Value"],
        table1Data: s.splice(0, Math.ceil(s.length / 2)),
        table2Data: s
    }
}


/**
 * QueueDetail page of web monitor.
 */
export default class QueueDetail extends Component {
    constructor(props) {
        super(props)
        this.refresh = this.refresh.bind(this)
        this.refreshInterval = this.refreshInterval.bind(this)
    }


    static propTypes = {
        queue: PropTypes.string.isRequired,
        showError: PropTypes.func.isRequired,
        setRefresh: PropTypes.func.isRequired,
        setRefreshInterval: PropTypes.func.isRequired
    };


    async componentDidMount() {
        this.props.setRefresh(this.refresh)
        this.props.setRefreshInterval(this.refreshInterval)
        this.refresh()
    }


    refresh() {
        updateComponent(this, '/queueDetail', {
            queue: this.props.queue
        })
    }


    refreshInterval() {
        return LONG_REFRESH_INTERVAL
    }


    render() {
        let tables = makeTableData(this.state)
        let pausedFormatter = {
            Paused: formatBoolean
        }
        return (
            <div>
                <Container>
                    <Row>
                        <Col md={12}>{
                            <Table columns={tables.queue.columns}
                                   data={tables.queue.data}
                                   caption={tables.queue.title}
                                   columnFormatter={pausedFormatter}/>
                        }</Col>
                    </Row>
                    <Row>
                        <Col md={12}>{
                            <Table columns={tables.errors.columns}
                                   data={tables.errors.data}
                                   caption={tables.errors.title}/>
                        }</Col>
                    </Row>
                    <Row>
                        <Col md={12}>{
                            // show only when events are found, that way
                            // the CollapsibleElement is only rendered "after"
                            // events are obtained from PMTA or otherwise it
                            // appears first thing when the page loads and then
                            // refreshes only to appear below other tables
                            // when data is obtained from PMTA giving the UI
                            // an odd, inconsistent look.
                            <CollapsibleElement label='Mode Change Events'>
                                <Table columns={tables.modeEvents.columns}
                                       data={tables.modeEvents.data}/>
                            </CollapsibleElement>
                        }</Col>
                    </Row>
                    <Row>
                        <Col md={12}>{
                            // show only when events are found, that way
                            // the CollapsibleElement is only rendered "after"
                            // events are obtained from PMTA or otherwise it
                            // appears first thing when the page loads and then
                            // refreshes only to appear below other tables
                            // when data is obtained from PMTA giving the UI
                            // an odd, inconsistent look.
                            <CollapsibleElement label='Recipient Events'>
                                <Table columns={tables.rcptEvents.columns}
                                       data={tables.rcptEvents.data}
                                       caption={tables.rcptEvents.title}/>
                            </CollapsibleElement>
                        }</Col>
                    </Row>
                    <Row>
                        <Col md={12}>{
                            <CollapsibleElement label='VMTA Settings'>
                                <Container>
                                    <Row>
                                        <Col md={6}>{
                                            <Table columns={tables.vmtaSettings.columns}
                                                   data={tables.vmtaSettings.table1Data}
                                                   caption={tables.vmtaSettings.title}/>
                                        }</Col>
                                        <Col md={6}>{
                                            <Table columns={tables.vmtaSettings.columns}
                                                   data={tables.vmtaSettings.table2Data}
                                                   caption={tables.vmtaSettings.title}/>
                                        }</Col>
                                    </Row>
                                </Container>
                            </CollapsibleElement>
                        }</Col>
                    </Row>
                    <Row>
                        <Col md={12}>{
                            <CollapsibleElement label='Queue Settings'>
                                <Container>
                                    <Row>
                                        <Col md={6}>{
                                            <Table columns={tables.queueSettings.columns}
                                                   data={tables.queueSettings.table1Data}
                                                   caption={tables.queueSettings.title}/>
                                        }</Col>
                                        <Col md={6}>{
                                            <Table columns={tables.queueSettings.columns}
                                                   data={tables.queueSettings.table2Data}
                                                   caption={tables.queueSettings.title}/>
                                        }</Col>
                                    </Row>
                                </Container>
                            </CollapsibleElement>
                        }</Col>
                    </Row>
                </Container>
            </div>
        );
    }
}
